/**
 * The deployment's API keys, as the browser sees them.
 *
 * There is one set, and it is the owner's. Visitors used to be able to paste
 * their own Cesium token and their own OpenRouter key into a Settings panel,
 * kept in local storage; both are gone. The owner sets every key once at
 * /admin, and this module only ever learns which of them are *set* — the
 * values stay on the server.
 *
 * The single exception is the Cesium Ion token, which Cesium must have in the
 * browser to draw anything. It does not come through here: it is fetched
 * before render by lib/publicConfig.ts, from an endpoint that serves that one
 * key and nothing else.
 *
 * Nothing here imports Cesium or React. `getCesiumIonToken` in
 * cesium-helpers/init reads through publicConfig, and that file must stay
 * free of the Cesium import so the token check does not pull the whole globe
 * into every route.
 */
import { API_BASE } from "@/lib/api/client";

export const SERVER_KEY_NAMES = [
  "firms_map_key",
  "waqi_token",
  "openrouter_api_key",
  "cesium_ion_token",
] as const;

export type ServerKeyName = (typeof SERVER_KEY_NAMES)[number];

export type KeyDef = {
  label: string;
  provider: string;
  /** What the key switches on, in the reader's words. */
  unlocks: string;
  /** Where to obtain one. */
  url: string;
};

export const KEY_DEFS: Record<ServerKeyName, KeyDef> = {
  cesium_ion_token: {
    label: "Cesium Ion access token",
    provider: "Cesium Ion",
    unlocks: "the 3D globe: world terrain and aerial imagery",
    url: "https://ion.cesium.com/tokens",
  },
  openrouter_api_key: {
    label: "OpenRouter API key",
    provider: "OpenRouter",
    unlocks: "the assistant",
    url: "https://openrouter.ai/keys",
  },
  firms_map_key: {
    label: "NASA FIRMS map key",
    provider: "NASA FIRMS",
    unlocks: "the satellite hotspots layer",
    url: "https://firms.modaps.eosdis.nasa.gov/api/map_key",
  },
  waqi_token: {
    label: "WAQI token",
    provider: "World Air Quality Index",
    unlocks: "the pollutant breakdown on the air-quality page",
    url: "https://aqicn.org/data-platform/token",
  },
};

// ── what the app is allowed to know ─────────────────────────────────────

export type ServerStatus = {
  /** Whether each key is set. Values are never returned to the browser. */
  keys: Record<ServerKeyName, boolean>;
  /** Always true now: changing a key requires an admin session. */
  writeProtected: boolean;
};

type StatusEnvelope = {
  data: { keys: Record<string, { configured: boolean }>; write_protected: boolean };
};

function toStatus(env: StatusEnvelope): ServerStatus {
  const keys = Object.fromEntries(
    SERVER_KEY_NAMES.map((name) => [name, Boolean(env.data.keys[name]?.configured)]),
  ) as ServerStatus["keys"];
  return { keys, writeProtected: Boolean(env.data.write_protected) };
}

/** Which keys are set. Read-only, unauthenticated, and free of values.
 *
 * This is what the "this feature needs a key" notices key off. Writing a key
 * is not possible from here at all — that lives in the admin panel, behind a
 * session cookie.
 */
export async function fetchServerStatus(): Promise<ServerStatus> {
  const res = await fetch(`${API_BASE}/api/settings/keys`, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) throw new Error(`settings/keys failed: HTTP ${res.status}`);
  return toStatus((await res.json()) as StatusEnvelope);
}
